import {
  DeliveryBarMachineContext,
  useCheckoutCartState,
} from '@monofront/business/contexts';
import React, { useEffect } from 'react';
import { formatPricePossibleFree, nl2br } from '@monofront/business/utils';
import Arrow from '@monofront/design-system/visuals/Icons/Old/Arrow';
import { IShippingMethod, ShippingMethodTypes } from '@monofront/types';
import Edit from '@monofront/design-system/visuals/Icons/Old/Edit';
import AddButtonWithLabel from '@monofront/design-system/components/AddButtonWithLabel';
import SelectPickup from './SelectPickup';
import SelectTimeSlot from './SelectTimeSlot';
import { useTranslations } from 'next-intl';
import useI18n from '@hooks/i18n/useI18n';
import { useCheckoutSelectAddressMachine } from '@context/checkoutCartMachineContext';
import {
  IPickupShippingMethod,
  SELECT_PICKUP_POINT_TRANSITIONS,
  SELECT_PICKUP_POINT_STATES,
  SELECT_SHIPPING_METHOD_TRANSITIONS,
  SELECT_SHIPPING_METHOD_STATES,
  SHIPPING_METHOD_TAG_COURIER_SELECTED,
} from '@monofront/business/stateMachines';
import Information from '@monofront/design-system/visuals/Icons/Information';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import Divider from '@monofront/design-system/components/Divider';
import RadioLine from '@monofront/design-system/components/Form/RadioLine';
import Textarea from '@monofront/design-system/components/Form/Textarea';
import useScrollToRefOnMount from '@hooks/useScrollToRefOnMount';
import { EVENTS } from '@monofront/business/stateMachines/deliveryBar';

const DELIVERY_INFOS_MAX_LENGTH = {
  [SELECT_SHIPPING_METHOD_STATES.COURIER_METHOD_SELECTED]: 128,
  [SELECT_SHIPPING_METHOD_STATES.TIME_SLOT_SELECTED]: 128,
  [SELECT_SHIPPING_METHOD_STATES.HOME_METHOD_SELECTED]: 32,
};

const CheckoutSelectShippingMethod = () => {
  const {
    selectAddressState,
    selectShippingMethodState,
    sendToSelectShippingMethod,
    selectPickupPointState,
    sendToSelectPickupPoint,
  } = useCheckoutSelectAddressMachine();

  const { send: sendToDeliveryBarMachine } =
    DeliveryBarMachineContext.useActorRef();

  const selectShippingRef = useScrollToRefOnMount();
  const myRelayPointRef = useScrollToRefOnMount();
  const deliveryInfoRef = useScrollToRefOnMount();

  const { shippingAddressSelected } = selectAddressState.context;
  const { methodsList, noShipment, methodSelectedCode } =
    selectShippingMethodState.context;

  const { pickupAddress } = selectPickupPointState.context;

  const tCheckout = useTranslations('Checkout');
  const tShipping = useTranslations('Shipping');
  const tShippingErrors = useTranslations('Shipping.Errors');
  const { checkoutCart, isScrollAvailable } = useCheckoutCartState();
  const { iso, isFrenchMarket } = useI18n();

  useEffect(() => {
    if (!methodSelectedCode) return;

    const selectedMethod = methodsList.find(
      (method) => method.code === methodSelectedCode,
    );

    if (selectedMethod) {
      if (selectedMethod.type === ShippingMethodTypes.NON_ADDRESSED) {
        return;
      }

      if (selectedMethod.type === ShippingMethodTypes.PICKUP) {
        sendToDeliveryBarMachine({
          type: EVENTS.OVERWRITE_SHIPPING_METHOD,
          shippingMethod: {
            type: selectedMethod.type,
            carrierId: methodSelectedCode as string,
            pickupPointCity: null,
            pickupPointId: null,
            pickupPointLabel: null,
            pickupPointStreet: null,
            pickupPointZipCode: null,
          },
        });
      } else {
        sendToDeliveryBarMachine({
          type: EVENTS.OVERWRITE_SHIPPING_METHOD,
          shippingMethod: {
            type: selectedMethod.type,
            carrierId: methodSelectedCode as string,
          },
        });
      }
    }
  }, [methodSelectedCode, methodsList, sendToDeliveryBarMachine]);

  if (noShipment) {
    return (
      <section>
        <h3 className="subtitle hxb">{tCheckout('deliveryMode')}</h3>
        <p className="error tw-lf-error-message">
          {checkoutCart?.isFresh || checkoutCart?.hasUltraFreshProducts
            ? tShippingErrors('shippingAddressNotEligibleForFreshAndUFShipping')
            : tShippingErrors('incompatibleProducts')}
        </p>
        <style jsx>{`
          section {
            margin-bottom: var(--space-5);
          }
        `}</style>
      </section>
    );
  }

  if (
    selectShippingMethodState.matches(
      SELECT_SHIPPING_METHOD_STATES.METHODS_FETCHING,
    )
  ) {
    return <HeartLoader />;
  }

  return (
    <>
      <section data-testid="Delivery modes section">
        <h3
          className="subtitle hxb"
          style={{ scrollMarginTop: 'var(--checkout-header-height)' }}
          ref={isScrollAvailable ? selectShippingRef : undefined}
        >
          {tCheckout('deliveryMode')}
        </h3>
        {methodsList.map((method) => {
          switch (method.type) {
            case ShippingMethodTypes.HOME:
            case ShippingMethodTypes.CLICK_AND_COLLECT:
              return (
                <li key={method.code}>
                  <RadioLine
                    id={method.id}
                    name=""
                    required
                    value={method.code}
                    checked={methodSelectedCode === method.code}
                    handleChecked={() => {
                      sendToSelectShippingMethod({
                        type: SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_HOME_METHOD,
                        method: method as IShippingMethod,
                        methodType: method.type as ShippingMethodTypes,
                      });
                    }}
                    more={
                      <span className="hxb price">
                        {formatPricePossibleFree(
                          method.price / 100,
                          tCheckout('free'),
                        )}
                      </span>
                    }
                    dataTestId={`CheckoutSelectShippingMethod-Button-shipping-method-${method.type}`}
                  >
                    <span className="hxb shipping-name">
                      {method.translations[iso].name}
                    </span>
                    <span
                      className="hxrt shipping-description"
                      dangerouslySetInnerHTML={{
                        __html: nl2br(method.translations[iso].description),
                      }}
                    ></span>
                  </RadioLine>
                </li>
              );
            case ShippingMethodTypes.PICKUP:
              return (
                <React.Fragment key={method.code}>
                  <li>
                    <RadioLine
                      handleChecked={() => {
                        sendToSelectShippingMethod({
                          type: SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_PICKUP_METHOD,
                          address: shippingAddressSelected!,
                          method: method as IPickupShippingMethod,
                          methodType: ShippingMethodTypes.PICKUP,
                        });
                      }}
                      id="pickup"
                      name=""
                      required
                      value={method.code}
                      checked={methodSelectedCode === method.code}
                      more={
                        <span className="hxb price">
                          {formatPricePossibleFree(
                            Math.min(
                              ...(method as IPickupShippingMethod).methods.map(
                                ({ price }) => price,
                              ),
                            ) / 100,
                            tCheckout('free'),
                          )}
                        </span>
                      }
                      dataTestId={`CheckoutSelectShippingMethod-Button-shipping-method-${method.type}`}
                    >
                      <span className="hxb shipping-name">
                        {
                          (method as IPickupShippingMethod).methods[0]
                            .translations[iso].name
                        }
                      </span>
                      <span
                        className="hxrt shipping-description"
                        dangerouslySetInnerHTML={{
                          __html: nl2br(
                            (method as IPickupShippingMethod)?.methods[0]
                              .translations[iso].description,
                          ),
                        }}
                      ></span>
                    </RadioLine>
                  </li>
                  {selectPickupPointState?.matches?.(
                    SELECT_PICKUP_POINT_STATES.OPENED,
                  ) && (
                    <SelectPickup
                      handleClose={() =>
                        sendToSelectPickupPoint(
                          SELECT_PICKUP_POINT_TRANSITIONS.CLOSE,
                        )
                      }
                      isOpen={selectPickupPointState?.matches?.(
                        SELECT_PICKUP_POINT_STATES.OPENED,
                      )}
                    />
                  )}
                </React.Fragment>
              );
            case ShippingMethodTypes.COURIER:
              method = method as IShippingMethod;

              return (
                <li key={method.code}>
                  <RadioLine
                    id={method.id}
                    name=""
                    required
                    value={method.code}
                    checked={methodSelectedCode === method.code}
                    handleChecked={() => {
                      sendToSelectShippingMethod({
                        type: SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_COURIER_METHOD,
                        checkoutCartId: checkoutCart!.id,
                        method: method as IShippingMethod,
                        methodType: ShippingMethodTypes.COURIER,
                      });
                    }}
                    more={
                      <span className="hxb price">
                        {formatPricePossibleFree(
                          method.price / 100,
                          tCheckout('free'),
                        )}
                      </span>
                    }
                    dataTestId={`CheckoutSelectShippingMethod-Button-shipping-method-${method.type}`}
                  >
                    <span className="hxb shipping-name">
                      <span className="courier-label">
                        {method.translations[iso].name}
                      </span>
                    </span>
                    <span
                      className="hxrt shipping-description"
                      dangerouslySetInnerHTML={{
                        __html: nl2br(method.translations[iso].description),
                      }}
                    ></span>
                  </RadioLine>
                </li>
              );
            default:
              return null;
          }
        })}
      </section>
      {selectShippingMethodState.matches(
        SELECT_SHIPPING_METHOD_STATES.PICKUP_METHOD_SELECTED,
      ) && (
        <>
          <Divider marginVertical="tw-my-6" />
          <section>
            <h3
              className="subtitle hxb"
              style={{ scrollMarginTop: 'var(--checkout-header-height)' }}
              ref={myRelayPointRef}
            >
              {tShipping('myRelayPoint')}
            </h3>
            <div className="pickup-selected-container">
              <div className="select-pickup-button">
                <AddButtonWithLabel
                  handleClick={() =>
                    sendToSelectPickupPoint({
                      type: SELECT_PICKUP_POINT_TRANSITIONS.OPEN,
                      address: shippingAddressSelected!,
                      methods: (
                        methodsList.find(
                          ({ type }) => type === ShippingMethodTypes.PICKUP,
                        ) as IPickupShippingMethod
                      )?.methods,
                    })
                  }
                >
                  {tShipping('choose')}
                </AddButtonWithLabel>
                <Arrow width={14} height={14} headDirection="right" />
              </div>
            </div>
          </section>
        </>
      )}
      {selectShippingMethodState.matches(
        SELECT_SHIPPING_METHOD_STATES.PICKUP_POINT_SELECTED,
      ) && (
        <>
          <Divider marginVertical="tw-my-6" />
          <section>
            <h3 className="subtitle hxb">{tShipping('myRelayPoint')}</h3>
            <div className="pickup-selected-container">
              <div>
                <div className="capitalize">
                  {pickupAddress!.company?.toLowerCase()}
                </div>
                <div className="capitalize">
                  {pickupAddress!.street?.toLowerCase()}
                </div>
                <div className="capitalize">
                  {pickupAddress!.postcode} {pickupAddress!.city?.toLowerCase()}
                </div>
              </div>
              <button
                className="pickup-selected-change"
                onClick={() => {
                  sendToSelectPickupPoint({
                    type: SELECT_PICKUP_POINT_TRANSITIONS.OPEN,
                    address: shippingAddressSelected!,
                    methods: (
                      methodsList.find(
                        ({ type }) => type === ShippingMethodTypes.PICKUP,
                      ) as IPickupShippingMethod
                    )?.methods,
                  });
                }}
              >
                <Edit />
              </button>
            </div>
          </section>
        </>
      )}
      {selectShippingMethodState.hasTag(
        SHIPPING_METHOD_TAG_COURIER_SELECTED,
      ) && (
        <>
          <Divider marginVertical="tw-my-6" />
          <section>
            <SelectTimeSlot />
          </section>
        </>
      )}
      {selectShippingMethodState.can({
        type: SELECT_SHIPPING_METHOD_TRANSITIONS.SET_DELIVERY_INFOS,
        deliveryInstruction: '',
      }) &&
        isFrenchMarket && ( // TODO:LF-2879 remove the flag isFrenchMarket when german market is ready
          <>
            <Divider marginVertical="tw-my-6" />
            <section>
              {/*
            @TODOAFTERTW
            I put ! before tw-mb-4 to override jsx css.
            Need to keep class to have the 1., 2., 3. etc..
            Remove it once we pass all checkout in TW
          */}
              <h3
                className="subtitle hxb !tw-mb-4"
                style={{ scrollMarginTop: 'var(--checkout-header-height)' }}
                ref={
                  !isScrollAvailable &&
                  !selectShippingMethodState.hasTag(
                    SHIPPING_METHOD_TAG_COURIER_SELECTED,
                  )
                    ? undefined
                    : deliveryInfoRef
                }
              >
                {tShipping('additionalDeliveryInformations')}
              </h3>
              <div className="tw-mb-2 tw-inline-flex tw-w-full tw-items-center tw-justify-start tw-gap-6 tw-rounded-2xl tw-bg-blue-100 tw-px-6 tw-py-4">
                <div className="tw-relative tw-hidden tw-h-6 tw-w-6 desktop:tw-block">
                  <Information size={IconSizes.md} />
                </div>
                <p className="tw-lf-body-regular-md">
                  {tShipping.rich('deliverInformationBlock', {
                    b: (children) => (
                      <span className="tw-lf-body-bold-md">{children}</span>
                    ),
                  })}
                </p>
              </div>
              <div>
                <h4 className="block-title">
                  {tShipping('furtherInformations')}
                </h4>
                <Textarea
                  name="Delivery Infos"
                  placeholder={tShipping('additionalInfosPlaceholder')}
                  id="deliveryInfos"
                  value={
                    selectShippingMethodState.context.shippingMethod
                      .deliveryInstruction ?? ''
                  }
                  onChange={(event) =>
                    sendToSelectShippingMethod({
                      type: SELECT_SHIPPING_METHOD_TRANSITIONS.SET_DELIVERY_INFOS,
                      deliveryInstruction: event.target.value,
                    })
                  }
                  maxLength={
                    DELIVERY_INFOS_MAX_LENGTH[
                      selectShippingMethodState.value as string
                    ] ?? 128
                  }
                  rows={3}
                  disableResize
                  dataTestId="CheckoutSelectShippingMethod-Textarea-delivery-info"
                  counterLimit={
                    DELIVERY_INFOS_MAX_LENGTH[
                      selectShippingMethodState.value as string
                    ]
                  }
                />
              </div>
            </section>
          </>
        )}
      <style jsx>{`
        li {
          list-style: none;
        }

        .block-title {
          all: inherit;
          margin: var(--space-2) 0;
        }

        .shipping-name {
          display: block;
        }

        .courier-label {
          margin-right: var(--space-2);
        }

        .shipping-description {
          display: block;
        }

        .price {
          text-align: right;
          display: flex;
          flex-direction: column;
          min-width: 60px;
        }

        .hxrt {
          color: var(--clr-grey-1);
        }

        .pickup-selected-change {
          cursor: pointer;
          display: flex;
          align-items: center;
          font-weight: var(--font-weight-bold);
          background-color: inherit;
          border: inherit;
        }

        .pickup-selected-container {
          display: flex;
          justify-content: space-between;
          margin: var(--space-4) var(--space-2);
        }

        .select-pickup-button {
          display: flex;
          cursor: pointer;
          align-items: center;
        }

        .select-pickup-button > :global(svg) {
          margin-left: var(--space-4);
        }
      `}</style>
    </>
  );
};

export default CheckoutSelectShippingMethod;
